package com.oscarherreros.appcalculo.game

/** Recorre todas las operaciones válidas entre dos fichas de [values]. */
private inline fun forEachMove(values: LongArray, action: (i: Int, j: Int, op: Op, result: Long) -> Unit) {
    for (i in values.indices) for (j in values.indices) {
        if (i == j) continue
        for (op in Op.entries) {
            // En las conmutativas basta con probar un orden.
            if (i > j && (op == Op.ADD || op == Op.MUL)) continue
            val result = op.calc(values[i], values[j]) ?: continue
            action(i, j, op, result)
        }
    }
}

/** Las fichas que quedan al sustituir las de las posiciones [i] y [j] por [result]. */
private fun LongArray.combine(i: Int, j: Int, result: Long): LongArray {
    val next = LongArray(size - 1)
    var k = 0
    for (m in indices) if (m != i && m != j) next[k++] = this[m]
    next[k] = result
    return next
}

/**
 * Para cada valor que se puede obtener operando con [numbers], el mínimo de
 * operaciones necesarias para llegar a él (0 para los propios números).
 */
fun reachableValues(numbers: List<Int>): Map<Long, Int> {
    val best = HashMap<Long, Int>()

    fun record(value: Long, steps: Int) {
        val previous = best[value]
        if (previous == null || steps < previous) best[value] = steps
    }

    fun explore(values: LongArray, steps: Int) {
        forEachMove(values) { i, j, _, result ->
            record(result, steps + 1)
            if (values.size > 2) explore(values.combine(i, j, result), steps + 1)
        }
    }

    val start = LongArray(numbers.size) { numbers[it].toLong() }
    start.forEach { record(it, 0) }
    explore(start, 0)
    return best
}

/**
 * Los pasos de una solución con el mínimo de operaciones para llegar a [target]
 * desde [numbers], o null si no se puede. Vacía si [target] ya está entre ellos.
 */
fun solve(numbers: List<Long>, target: Long): List<Step>? {
    if (target in numbers) return emptyList()
    val path = ArrayList<Step>()

    fun search(values: LongArray, limit: Int): Boolean {
        forEachMove(values) { i, j, op, result ->
            path.add(Step(values[i], op, values[j], result))
            if (result == target) return true
            if (limit > 1 && search(values.combine(i, j, result), limit - 1)) return true
            path.removeAt(path.lastIndex)
        }
        return false
    }

    // Se prueba con una operación, luego con dos…: la primera solución que sale es la más corta.
    val start = numbers.toLongArray()
    for (limit in 1 until numbers.size) {
        if (search(start, limit)) return path
    }
    return null
}
